
/** The value the exporter writes for "this cell has no texture". It is not
 *  remapped to a layer anywhere: a cell with no ground texture is drawn as a
 *  neutral fill, because inventing a layer for it would hide export bugs behind
 *  plausible-looking grass. */
export const NO_TEXTURE = 0;

const MAGIC = 0x4d335447; // 'M3TG'
const VERSION = 1;
export const TILE_HEADER_BYTES = 24;
export const TILE_FETCH_RETRIES = 1;
export const TILE_RETRY_DELAY_MS = 250;

export class TileMapError extends Error {}

export interface TileHeader {
  readonly version: number;
  readonly layers: number;
  readonly width: number;
  readonly height: number;
  readonly compressedBytes: number;
  readonly cellCount: number;
}

export function parseTileHeader(bytes: Uint8Array): TileHeader {
  if (bytes.byteLength < TILE_HEADER_BYTES) {
    throw new TileMapError(`tile blob is ${bytes.byteLength} bytes, shorter than its header`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, false) !== MAGIC) {
    throw new TileMapError("tile blob has the wrong magic");
  }
  const version = view.getUint16(4, true);
  if (version !== VERSION) {
    throw new TileMapError(`tile blob version ${version}, expected ${VERSION}`);
  }
  const layers = view.getUint16(6, true);
  const width = view.getUint32(8, true);
  const height = view.getUint32(12, true);
  const compressedBytes = view.getUint32(16, true);
  const cellCount = view.getUint32(20, true);

  if (layers < 1) throw new TileMapError("tile blob declares no atlas layers");
  if (width < 1 || height < 1) {
    throw new TileMapError(`tile blob is ${width}x${height} cells`);
  }
  // The cell count is redundant with the dimensions and is checked against them
  // rather than trusted: a blob whose payload is a row short still inflates, and
  // then the whole map is textured one row out with nothing to point at.
  if (width * height !== cellCount) {
    throw new TileMapError(
      `tile blob is ${width}x${height} cells but declares ${cellCount} of them`,
    );
  }
  if (bytes.byteLength < TILE_HEADER_BYTES + compressedBytes) {
    throw new TileMapError(
      `tile blob is truncated: ${bytes.byteLength - TILE_HEADER_BYTES} of ` +
        `${compressedBytes} compressed bytes present`,
    );
  }
  return { version, layers, width, height, compressedBytes, cellCount };
}

export function tileLayer(index: number, layers: number): number {
  if (!Number.isInteger(index) || index <= NO_TEXTURE) return -1;
  if (index > layers) return -1;
  return index - 1;
}

/** Per-cell texture indices for the whole map. */
export class TileMap {
  readonly width: number;
  readonly height: number;
  readonly layers: number;
  private readonly indices: Uint8Array;

  constructor(options: {
    width: number;
    height: number;
    layers: number;
    indices: Uint8Array;
  }) {
    const expected = options.width * options.height;
    if (options.indices.length !== expected) {
      throw new TileMapError(
        `tile grid is ${options.indices.length} cells, expected ${expected}`,
      );
    }
    this.width = options.width;
    this.height = options.height;
    this.layers = options.layers;
    this.indices = options.indices;
  }

  /** The stored index at a cell. Out of bounds reads as no texture, which is what
   *  the renderer draws at the edge of the world anyway. */
  indexAt(cellX: number, cellY: number): number {
    if (cellX < 0 || cellY < 0 || cellX >= this.width || cellY >= this.height) {
      return NO_TEXTURE;
    }
    return this.indices[cellY * this.width + cellX];
  }

  /** The atlas layer at a cell, or -1 where there is none. */
  layerAt(cellX: number, cellY: number): number {
    return tileLayer(this.indexAt(cellX, cellY), this.layers);
  }

  shaderIndexAt(cellX: number, cellY: number): number {
    return this.layerAt(cellX, cellY) + 1;
  }

  static async decode(blob: ArrayBuffer | Uint8Array): Promise<TileMap> {
    const bytes = blob instanceof Uint8Array ? blob : new Uint8Array(blob);
    const header = parseTileHeader(bytes);
    // Copied into its own buffer rather than viewed in place: the stream APIs
    // below only accept views that are known not to sit on a `SharedArrayBuffer`,
    // and a quarter-megabyte copy happens once per map load.
    const body = new Uint8Array(header.compressedBytes);
    body.set(
      bytes.subarray(TILE_HEADER_BYTES, TILE_HEADER_BYTES + header.compressedBytes),
    );

    let indices: Uint8Array;
    try {
      indices = await inflate(body);
    } catch (error) {
      throw new TileMapError(`tile blob payload is not valid gzip: ${String(error)}`);
    }
    if (indices.length !== header.cellCount) {
      throw new TileMapError(
        `tile blob inflates to ${indices.length} cells, header says ${header.cellCount}`,
      );
    }

    return new TileMap({
      width: header.width,
      height: header.height,
      layers: header.layers,
      indices,
    });
  }

  static async fetch(url: string): Promise<TileMap> {
    // `tools/build_terrain_textures.mjs` emits this tile blob beside the terrain
    // atlas. A transient Safari request interruption must not discard the
    // entire WebGL scene when a fresh fetch can recover it, but a missing or
    // malformed blob still needs to reach createRenderer's Canvas2D fallback.
    let failure: unknown;
    for (let attempt = 0; attempt <= TILE_FETCH_RETRIES; attempt++) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new TileMapError(`${url}: HTTP ${response.status}`);
        return TileMap.decode(await response.arrayBuffer());
      } catch (error) {
        failure = error;
        if (attempt === TILE_FETCH_RETRIES) break;
        await new Promise<void>((resolve) => setTimeout(resolve, TILE_RETRY_DELAY_MS));
      }
    }
    throw failure;
  }
}

/** GZIP in, bytes out, using the platform's own inflate. No inflate
 *  implementation ships with the client; every target browser has this one. */
async function inflate(body: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const source = new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(controller) {
      controller.enqueue(body);
      controller.close();
    },
  });
  const inflated = source.pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(inflated).arrayBuffer());
}
